import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, FileText, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { supabase } from "@/lib/supabase";
import { glassCardClass, glassMutedCardClass } from "@/recruitment/styles.js";

const MAIN_APP_LOGIN_URL = import.meta.env.VITE_MAIN_APP_LOGIN_URL || "https://kconnect.pl/login";

const statusLabels = {
  draft: "Szkic",
  scheduled: "Zaplanowana",
  intake_open: "Nabór otwarty",
  intake_closed: "Nabór zamknięty",
  published: "Opublikowana",
  archived: "Archiwum",
};

const statusFlow = [
  { id: "draft", label: "Szkic", description: "Konfiguracja kampanii jest przygotowywana." },
  { id: "scheduled", label: "Zaplanowana", description: "Nabór jeszcze się nie rozpoczął." },
  { id: "intake_open", label: "Nabór otwarty", description: "Rodzice mogą składać i edytować wnioski." },
  { id: "intake_closed", label: "Nabór zamknięty", description: "Wnioski są zamknięte, trwa weryfikacja i scoring." },
  { id: "published", label: "Opublikowana", description: "Wyniki kampanii zostały opublikowane." },
  { id: "archived", label: "Archiwum", description: "Kampania zakończona i zamknięta." },
];

function formatDate(value) {
  if (!value) return "Brak";
  return new Date(value).toLocaleString();
}

export default function AdminCampaignDetailPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState(false);
  const [profile, setProfile] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [scoringModel, setScoringModel] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [applications, setApplications] = useState([]);

  const canManageRecruitment = useMemo(() => {
    if (!profile) return false;
    const roles = profile.roles || [];
    return roles.includes("branch_admin") || roles.includes("super_admin");
  }, [profile]);
  const isSuperAdmin = useMemo(() => (profile?.roles || []).includes("super_admin"), [profile]);

  const pendingRequests = useMemo(
    () => applications.filter((item) => item.status === "submitted" || item.status === "under_verification"),
    [applications]
  );

  const getSessionFromHash = () => {
    const rawHash = window.location.hash || "";
    if (!rawHash.startsWith("#")) return null;

    const params = new URLSearchParams(rawHash.slice(1));
    const accessToken = params.get("access_token");
    const refreshToken = params.get("refresh_token");

    if (!accessToken || !refreshToken) return null;

    return {
      access_token: accessToken,
      refresh_token: refreshToken,
    };
  };

  const clearHash = () => {
    const cleanUrl = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState({}, document.title, cleanUrl);
  };

  const redirectToMainLogin = () => {
    const targetUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
    try {
      const loginUrl = new URL(MAIN_APP_LOGIN_URL, window.location.origin);
      loginUrl.searchParams.set("next", targetUrl);
      window.location.href = loginUrl.toString();
      return;
    } catch (_) {
      const fallback = new URL("https://kconnect.pl/login");
      fallback.searchParams.set("next", targetUrl);
      window.location.href = fallback.toString();
    }
  };

  const loadCampaignData = useCallback(async () => {
    const { data: campaignData, error: campaignError } = await supabase
      .from("recruitment_campaigns")
      .select(
        "id, branch_id, campaign_name, public_slug, status, intake_start_at, intake_end_at, decision_publish_at, capacity, scoring_model_id, tie_breaker_config, settings"
      )
      .eq("id", campaignId)
      .maybeSingle();
    if (campaignError) throw campaignError;
    if (!campaignData) throw new Error("Nie znaleziono kampanii.");

    const { data: documentsData, error: documentsError } = await supabase
      .from("recruitment_campaign_documents")
      .select("*")
      .eq("campaign_id", campaignId)
      .order("created_at", { ascending: true });
    if (documentsError) throw documentsError;

    const { data: applicationsData, error: applicationsError } = await supabase
      .from("recruitment_applications")
      .select("id, child_first_name, child_last_name, child_date_of_birth, status, submitted_at, updated_at, verification_comment")
      .eq("campaign_id", campaignId)
      .order("submitted_at", { ascending: true });
    if (applicationsError) throw applicationsError;

    setCampaign(campaignData);
    setDocuments(documentsData ?? []);
    setApplications(applicationsData ?? []);

    if (campaignData.scoring_model_id) {
      const { data: scoringData, error: scoringError } = await supabase
        .from("recruitment_scoring_models")
        .select("id, name, description, rules, tie_breaker_options")
        .eq("id", campaignData.scoring_model_id)
        .maybeSingle();
      if (scoringError) throw scoringError;
      setScoringModel(scoringData ?? null);
    } else {
      setScoringModel(null);
    }
  }, [campaignId]);

  useEffect(() => {
    const bootstrap = async () => {
      try {
        let user = null;

        const {
          data: { user: existingUser },
          error: authError,
        } = await supabase.auth.getUser();
        if (authError && !String(authError.message || "").toLowerCase().includes("auth session missing")) {
          throw authError;
        }
        user = existingUser;

        if (!user) {
          const hashSession = getSessionFromHash();
          if (hashSession) {
            const { error: setSessionError } = await supabase.auth.setSession(hashSession);
            if (setSessionError) throw setSessionError;
            clearHash();
            const {
              data: { user: userAfterSessionSet },
              error: refreshUserError,
            } = await supabase.auth.getUser();
            if (refreshUserError) throw refreshUserError;
            user = userAfterSessionSet;
          }
        }

        if (!user) {
          redirectToMainLogin();
          return;
        }

        const { data: profileData, error: profileError } = await supabase
          .from("app_users")
          .select("id, roles, branch_ids")
          .eq("id", user.id)
          .maybeSingle();
        if (profileError) throw profileError;
        setProfile(profileData ?? null);

        await loadCampaignData();
      } catch (err) {
        const message = String(err?.message || "");
        if (message.toLowerCase().includes("auth session missing")) {
          redirectToMainLogin();
          return;
        }
        toast.error(message || "Nie udało się załadować kampanii.");
        navigate("/admin", { replace: true });
      } finally {
        setLoading(false);
      }
    };

    bootstrap();
  }, [loadCampaignData, navigate]);

  const verifyApplication = async (applicationId, nextStatus) => {
    const verificationComment = window.prompt("Komentarz (opcjonalnie):", "") || "";

    setBusyAction(true);
    try {
      const { error } = await supabase.rpc("recruitment_verify_application", {
        p_application_id: applicationId,
        p_new_status: nextStatus,
        p_comment: verificationComment || null,
      });
      if (error) throw error;

      toast.success("Status wniosku został zaktualizowany.");
      await loadCampaignData();
    } catch (err) {
      toast.error(err.message || "Nie udało się zaktualizować statusu wniosku.");
    } finally {
      setBusyAction(false);
    }
  };

  const runScoring = async () => {
    setBusyAction(true);
    try {
      const { error } = await supabase.rpc("recruitment_run_scoring", {
        p_campaign_id: campaignId,
      });
      if (error) throw error;
      toast.success("Scoring został uruchomiony.");
    } catch (err) {
      toast.error(err.message || "Nie udało się uruchomić scoringu.");
    } finally {
      setBusyAction(false);
    }
  };

  const publishResults = async () => {
    setBusyAction(true);
    try {
      const { error } = await supabase.rpc("recruitment_publish_results", {
        p_campaign_id: campaignId,
      });
      if (error) throw error;
      toast.success("Wyniki zostały opublikowane.");
      await loadCampaignData();
    } catch (err) {
      toast.error(err.message || "Nie udało się opublikować wyników.");
    } finally {
      setBusyAction(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-slate-500">Ładowanie kampanii...</p>;
  }

  if (!canManageRecruitment) {
    return (
      <Card className={glassCardClass}>
        <CardHeader>
          <CardTitle>Brak dostępu</CardTitle>
          <CardDescription>Ta sekcja jest dostępna tylko dla administratorów rekrutacji.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (!campaign) {
    return (
      <Card className={glassCardClass}>
        <CardHeader>
          <CardTitle>Brak kampanii</CardTitle>
          <CardDescription>Nie udało się odnaleźć wybranej kampanii.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const hasBranchAccess = isSuperAdmin || (profile?.branch_ids || []).includes(campaign.branch_id);
  if (!hasBranchAccess) {
    return (
      <Card className={glassCardClass}>
        <CardHeader>
          <CardTitle>Brak dostępu do kampanii</CardTitle>
          <CardDescription>Kampania należy do innej placówki niż aktualny zakres Twoich uprawnień.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const schoolYear = campaign.settings?.schoolYear || "Nie ustawiono";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button asChild variant="outline" size="sm">
          <Link to="/admin" className="inline-flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            Wróć do kampanii
          </Link>
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={runScoring} disabled={busyAction} className="inline-flex items-center gap-2">
            {busyAction ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Przelicz scoring
          </Button>
          <Button onClick={publishResults} disabled={busyAction}>
            Opublikuj wyniki
          </Button>
        </div>
      </div>

      <Card className={glassCardClass}>
        <CardHeader>
          <CardTitle>{campaign.campaign_name}</CardTitle>
          <CardDescription>Rok szkolny: {schoolYear}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  Status: {statusLabels[campaign.status] || campaign.status}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Mapa statusów kampanii</DialogTitle>
                  <DialogDescription>Aktualny status jest wyróżniony.</DialogDescription>
                </DialogHeader>
                <div className="space-y-2">
                  {statusFlow.map((item) => {
                    const isCurrent = item.id === campaign.status;
                    return (
                      <div
                        key={item.id}
                        className={`rounded-md border p-3 ${isCurrent ? "border-blue-300 bg-blue-100/45" : glassMutedCardClass}`}
                      >
                        <p className="font-medium">
                          {item.label} {isCurrent ? "(aktualny)" : ""}
                        </p>
                        <p className="text-sm text-slate-600">{item.description}</p>
                      </div>
                    );
                  })}
                </div>
              </DialogContent>
            </Dialog>
            <Badge variant="outline">Miejsca: {campaign.capacity}</Badge>
            <Badge variant="outline">Slug: {campaign.public_slug}</Badge>
            <Badge>Liczba wniosków: {applications.length}</Badge>
            <Badge>Do weryfikacji: {pendingRequests.length}</Badge>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            <Card className={glassMutedCardClass}>
              <CardHeader>
                <CardTitle className="text-base">Start procesu</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-700">{formatDate(campaign.intake_start_at)}</p>
              </CardContent>
            </Card>
            <Card className={glassMutedCardClass}>
              <CardHeader>
                <CardTitle className="text-base">Koniec przyjmowania wniosków</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-700">{formatDate(campaign.intake_end_at)}</p>
              </CardContent>
            </Card>
            <Card className={glassMutedCardClass}>
              <CardHeader>
                <CardTitle className="text-base">Publikacja wyników</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-700">{formatDate(campaign.decision_publish_at)}</p>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="requests">
        <TabsList>
          <TabsTrigger value="requests">Wnioski</TabsTrigger>
          <TabsTrigger value="documents">Dokumenty / formularze</TabsTrigger>
          <TabsTrigger value="scoring">Konfiguracja punktacji</TabsTrigger>
        </TabsList>

        <TabsContent value="requests">
          <Card className={glassCardClass}>
            <CardHeader>
              <CardTitle>Requests</CardTitle>
              <CardDescription>Lista wniosków oczekujących na weryfikację oraz historia decyzji.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {applications.length === 0 ? (
                <p className="text-sm text-slate-500">Brak wniosków dla tej kampanii.</p>
              ) : (
                applications.map((application) => {
                  const actionable = application.status === "submitted" || application.status === "under_verification";
                  return (
                    <div key={application.id} className={`flex flex-col gap-2 rounded-lg p-3 md:flex-row md:items-center md:justify-between ${glassMutedCardClass}`}>
                      <div className="space-y-1">
                        <p className="font-medium">
                          {application.child_first_name} {application.child_last_name}
                        </p>
                        <p className="text-xs text-slate-500">
                          Data ur.: {application.child_date_of_birth || "brak"} | Złożony: {formatDate(application.submitted_at)}
                        </p>
                        {application.verification_comment ? (
                          <p className="text-xs text-slate-600">Komentarz: {application.verification_comment}</p>
                        ) : null}
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline">status: {application.status}</Badge>
                        {actionable ? (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => verifyApplication(application.id, "approved_verification")}
                              disabled={busyAction}
                              className="inline-flex items-center gap-1"
                            >
                              <CheckCircle2 className="h-4 w-4" />
                              Zweryfikuj
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => verifyApplication(application.id, "rejected_verification")}
                              disabled={busyAction}
                              className="inline-flex items-center gap-1"
                            >
                              <XCircle className="h-4 w-4" />
                              Odrzuć
                            </Button>
                          </>
                        ) : null}
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents">
          <Card className={glassCardClass}>
            <CardHeader>
              <CardTitle>Dokumenty / formularze kampanii</CardTitle>
              <CardDescription>Dokumenty i zgody wymagane podczas procesu aplikacji.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {documents.length === 0 ? (
                <p className="text-sm text-slate-500">Brak skonfigurowanych dokumentów.</p>
              ) : (
                documents.map((document) => (
                  <div key={document.id} className={`rounded-md p-3 ${glassMutedCardClass}`}>
                    <p className="font-medium inline-flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      {document.title || document.name || document.document_type || "Dokument"}
                    </p>
                    <p className="text-xs text-slate-500">Typ: {document.document_type}</p>
                    <p className="text-xs text-slate-500">Wymagany: {document.required ? "tak" : "nie"}</p>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="scoring">
          <Card className={glassCardClass}>
            <CardHeader>
              <CardTitle>Konfiguracja punktacji</CardTitle>
              <CardDescription>Model punktacji i tie-breakery przypisane do kampanii (zamrożone dla kampanii).</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {scoringModel ? (
                <>
                  <p className="text-sm">
                    <span className="font-medium">Model:</span> {scoringModel.name}
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">Opis:</span> {scoringModel.description || "Brak"}
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">Tie-breakers:</span>{" "}
                    {Array.isArray(campaign.tie_breaker_config) ? campaign.tie_breaker_config.join(", ") : "Brak"}
                  </p>
                  <pre className={`overflow-auto rounded-md p-3 text-xs ${glassMutedCardClass}`}>
                    {JSON.stringify(scoringModel.rules || {}, null, 2)}
                  </pre>
                </>
              ) : (
                <p className="text-sm text-slate-500">Brak przypisanego modelu punktacji.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
